import { enumApi } from '../api/services';

/** erpa_base_tbl code_type */
export const IPC_ENUM = {
  PROJECT_STATUS: 'IPC_PROJECT_STATUS',
  MILESTONE_STATUS: 'IPC_MILESTONE_STATUS',
  MILESTONE_PHASE: 'IPC_MILESTONE_PHASE',
  SPEC_STATUS: 'IPC_SPEC_STATUS',
  NODE_COMPLETION_METHOD: 'IPC_NODE_COMPLETION_METHOD',
  TERMINATE_REASON: 'IPC_TERMINATE_REASON',
} as const;

export type IpcEnumItem = {
  code_type: string;
  value: string;
  name: string;
  sort: number;
};

type EnumBucket = {
  items: IpcEnumItem[];
  nameByValue: Map<string, string>;
  valueByName: Map<string, string>;
};

const buckets = new Map<string, EnumBucket>();
let loadPromise: Promise<void> | null = null;
let loaded = false;
const listeners = new Set<() => void>();

function emptyBucket(): EnumBucket {
  return {
    items: [],
    nameByValue: new Map(),
    valueByName: new Map(),
  };
}

function setBucket(codeType: string, items: IpcEnumItem[]) {
  const nameByValue = new Map<string, string>();
  const valueByName = new Map<string, string>();
  const normalized = [...items]
    .filter(x => x.value)
    .sort((a, b) => (a.sort || 0) - (b.sort || 0));
  for (const item of normalized) {
    const value = String(item.value).trim();
    const name = String(item.name ?? item.value).trim() || value;
    if (!nameByValue.has(value.toLowerCase())) {
      nameByValue.set(value.toLowerCase(), name);
    }
    if (!valueByName.has(name.toLowerCase())) {
      valueByName.set(name.toLowerCase(), value);
    }
  }
  buckets.set(codeType, { items: normalized, nameByValue, valueByName });
}

function notify() {
  listeners.forEach(fn => {
    try {
      fn();
    } catch {
      /* ignore */
    }
  });
}

/** 订阅枚举加载完成 / 刷新（用于组件重渲染） */
export function subscribeIpcStatusEnums(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function isIpcStatusEnumsLoaded(): boolean {
  return loaded;
}

/** 拉取项目/节点/规格状态及节点完成方式枚举（可重复调用，单飞） */
export async function ensureIpcStatusEnumsLoaded(): Promise<void> {
  const codeTypes = [
    IPC_ENUM.PROJECT_STATUS,
    IPC_ENUM.MILESTONE_STATUS,
    IPC_ENUM.MILESTONE_PHASE,
    IPC_ENUM.SPEC_STATUS,
    IPC_ENUM.NODE_COMPLETION_METHOD,
    IPC_ENUM.TERMINATE_REASON,
  ];
  const missing = codeTypes.filter(ct => !buckets.has(ct));
  if (loaded && missing.length === 0) return;
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    const toLoad = missing.length > 0 ? missing : codeTypes;
    await Promise.all(
      toLoad.map(async codeType => {
        try {
          const { list } = await enumApi.getList(codeType);
          const items = (list ?? [])
            .filter(x => !x.code_type || x.code_type === codeType)
            .map((x, i) => ({
              code_type: x.code_type || codeType,
              value: String(x.value ?? '').trim(),
              name: String(x.name ?? x.value ?? '').trim(),
              sort: typeof x.sort === 'number' ? x.sort : i + 1,
            }))
            .filter(x => x.value);
          setBucket(codeType, items);
        } catch (e) {
          console.error(`加载枚举 ${codeType} 失败`, e);
          setBucket(codeType, []);
        }
      }),
    );
    loaded = true;
    notify();
  })().finally(() => {
    loadPromise = null;
  });

  return loadPromise;
}

function getBucket(codeType: string): EnumBucket {
  return buckets.get(codeType) ?? emptyBucket();
}

/** value → 中文名；未命中返回 value 或 fallback */
export function getIpcEnumName(
  codeType: string,
  value?: string | null,
  fallback = '—',
): string {
  const v = (value ?? '').trim();
  if (!v) return fallback;
  const name = getBucket(codeType).nameByValue.get(v.toLowerCase());
  return name || v;
}

/** 中文名 → value（兼容历史数据）；找不到返回 null */
export function resolveIpcEnumValue(codeType: string, raw?: string | null): string | null {
  const s = (raw ?? '').trim();
  if (!s) return null;
  const bucket = getBucket(codeType);
  if (bucket.nameByValue.has(s.toLowerCase())) return s;
  const byName = bucket.valueByName.get(s.toLowerCase());
  return byName ?? null;
}

export function getIpcEnumOptions(codeType: string): { value: string; label: string }[] {
  return getBucket(codeType).items.map(x => ({
    value: x.value,
    label: x.name || x.value,
  }));
}

export function projectStatusLabel(status?: string | null): string {
  return getIpcEnumName(IPC_ENUM.PROJECT_STATUS, status);
}

export function milestoneStatusLabel(status?: string | null): string {
  return getIpcEnumName(IPC_ENUM.MILESTONE_STATUS, status);
}

export function specStatusLabel(status?: string | null): string {
  return getIpcEnumName(IPC_ENUM.SPEC_STATUS, status);
}

export function projectStatusOptions(): { value: string; label: string }[] {
  return getIpcEnumOptions(IPC_ENUM.PROJECT_STATUS);
}

export function milestoneStatusOptions(): { value: string; label: string }[] {
  return getIpcEnumOptions(IPC_ENUM.MILESTONE_STATUS);
}

export function specStatusOptions(): { value: string; label: string }[] {
  return getIpcEnumOptions(IPC_ENUM.SPEC_STATUS);
}

const FALLBACK_TERMINATE_REASONS = [
  '市场同质化/差异化不足',
  '品牌/品类战略调整',
  '品牌产品线复盘叫停',
  '渠道卖进变化',
  '配方技术瓶颈无法突破',
  '核心原料受限无替代',
  '明星/IP/舆情等外部因素',
  '其他（需备注）',
];

export function terminateReasonOptions(): { value: string; label: string }[] {
  const fromBase = getIpcEnumOptions(IPC_ENUM.TERMINATE_REASON);
  if (fromBase.length > 0) return fromBase;
  return FALLBACK_TERMINATE_REASONS.map(text => ({ value: text, label: text }));
}

export function terminateReasonRequiresRemark(reason?: string | null): boolean {
  const r = (reason ?? '').trim();
  if (!r) return false;
  if (r.toLowerCase() === 'other') return true;
  return r.includes('需备注');
}

export function nodeCompletionMethodLabel(value?: string | null): string {
  return getIpcEnumName(IPC_ENUM.NODE_COMPLETION_METHOD, value);
}

export function nodeCompletionMethodOptions(): { value: string; label: string }[] {
  const fromBase = getIpcEnumOptions(IPC_ENUM.NODE_COMPLETION_METHOD);
  if (fromBase.length > 0) return fromBase;
  // 枚举未加载时仅返回英文 value，避免写死中文
  return [
    { value: 'manual', label: 'manual' },
    { value: 'auto', label: 'auto' },
  ];
}

export function milestonePhaseLabel(value?: string | null): string {
  return getIpcEnumName(IPC_ENUM.MILESTONE_PHASE, value);
}

/** 里程碑所属阶段；无枚举时返回空，禁止前端写死阶段列表 */
export function milestonePhaseOptions(): { value: string; label: string }[] {
  return getIpcEnumOptions(IPC_ENUM.MILESTONE_PHASE);
}
